/**
 * @vuelto-surface
 * mobile: ok
 * desktop: ok
 * strategy: inherit
 */
import React from 'react';
import { StyleSheet, Text, View } from 'react-native';
import { Ionicons } from '@expo/vector-icons';
import { ticketColors, ticketFonts } from '../../../shared/theme/ticketColors';
import { formatOrderDisplayCode, type CustomerOrderDetail } from '../../domain/orderTypes';

type Props = {
  order: CustomerOrderDetail;
  /** Rider assignment status — hint when assigned + pickup gate. */
  riderAssignmentStatus?: string | null;
};

function itemLabel(item: CustomerOrderDetail['items'][number]): string {
  if (item.item_status === 'replaced' && item.effective_product_name) {
    return item.effective_product_name;
  }
  return item.product_name;
}

/** Bloque handoff en ready: código grande + checklist para armar la bolsa. */
export function OrderHandoffBlock({ order, riderAssignmentStatus }: Props) {
  if (order.status !== 'ready') return null;

  const code = formatOrderDisplayCode(order.display_code, order.id);
  const items = order.items.filter((i) => i.item_status !== 'removed');
  const showRiderHint =
    order.pickup_verification_required && riderAssignmentStatus === 'assigned';

  return (
    <View style={styles.card}>
      <Text style={styles.copy}>Identificá la bolsa con este código</Text>
      <Text style={styles.code} accessibilityRole="header">
        {code}
      </Text>
      <Text style={styles.customer}>{order.customer_name}</Text>
      {showRiderHint ? (
        <View style={styles.hintRow}>
          <Ionicons name="bicycle-outline" size={16} color={ticketColors.gold} />
          <Text style={styles.hint}>El repartidor tiene que confirmar este código al retirar.</Text>
        </View>
      ) : null}
      <View style={styles.list}>
        {items.map((item) => (
          <View key={item.id} style={styles.row}>
            <Ionicons name="square-outline" size={16} color={ticketColors.ink2} />
            <Text style={styles.item}>
              {item.quantity}× {itemLabel(item)}
            </Text>
          </View>
        ))}
      </View>
    </View>
  );
}

const styles = StyleSheet.create({
  card: {
    backgroundColor: ticketColors.paper,
    borderRadius: 10,
    borderWidth: 1,
    borderColor: ticketColors.ruleSoft,
    padding: 16,
    gap: 8,
    alignItems: 'center',
  },
  copy: {
    fontSize: 13,
    fontWeight: '600',
    color: ticketColors.ink3,
    fontFamily: ticketFonts.sans,
    textAlign: 'center',
  },
  code: {
    fontSize: 48,
    fontWeight: '800',
    letterSpacing: 6,
    color: ticketColors.ink,
    fontFamily: ticketFonts.sans,
    lineHeight: 56,
  },
  customer: {
    fontSize: 16,
    fontWeight: '700',
    color: ticketColors.ink,
    fontFamily: ticketFonts.sans,
  },
  hintRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 6,
    backgroundColor: ticketColors.paper2,
    paddingHorizontal: 10,
    paddingVertical: 8,
    borderRadius: 8,
    alignSelf: 'stretch',
  },
  hint: {
    flex: 1,
    fontSize: 13,
    fontWeight: '600',
    color: ticketColors.ink2,
    fontFamily: ticketFonts.sans,
  },
  list: { alignSelf: 'stretch', gap: 6, marginTop: 4 },
  row: { flexDirection: 'row', alignItems: 'center', gap: 8 },
  item: {
    flex: 1,
    fontSize: 14,
    fontWeight: '600',
    color: ticketColors.ink,
    fontFamily: ticketFonts.sans,
  },
});
